/**
 * Applications & consent checks (C10) — ported from x365 modules/apps.js into pure
 * functions emitting ControlResult[] for the evidence engine.
 */
import type { AffectedObject, ControlResult } from '@/compliance/evidence/types';
import {
  APP_ENDPOINTS,
  getApplications,
  getOAuth2PermissionGrants,
  getServicePrincipals,
  type AppRegistration,
  type OAuthGrant,
  type ServicePrincipalRef,
} from '@/graph/modules/applications';

const EXPIRY_WARN_DAYS = 30;

const HIGH_RISK_SCOPES = new Set<string>([
  'Directory.ReadWrite.All',
  'Directory.AccessAsUser.All',
  'RoleManagement.ReadWrite.Directory',
  'AppRoleAssignment.ReadWrite.All',
  'Application.ReadWrite.All',
  'Application.ReadWrite.OwnedBy',
  'User.ReadWrite.All',
  'Group.ReadWrite.All',
  'GroupMember.ReadWrite.All',
  'Mail.ReadWrite',
  'Mail.Send',
  'Files.ReadWrite.All',
  'Sites.ReadWrite.All',
  'Sites.FullControl.All',
  'full_access_as_user',
  'EWS.AccessAsUser.All',
  'Policy.ReadWrite.ConditionalAccess',
  'PrivilegedAccess.ReadWrite.AzureAD',
]);

export interface ApplicationsData {
  apps: AppRegistration[];
  grants: OAuthGrant[];
  servicePrincipals: ServicePrincipalRef[];
}

export async function collectApplicationsData(): Promise<ApplicationsData> {
  const [apps, grants, servicePrincipals] = await Promise.all([
    getApplications().catch(() => [] as AppRegistration[]),
    getOAuth2PermissionGrants().catch(() => [] as OAuthGrant[]),
    getServicePrincipals().catch(() => [] as ServicePrincipalRef[]),
  ]);
  return { apps, grants, servicePrincipals };
}

export function evaluateApplications(data: ApplicationsData): ControlResult[] {
  const { apps, grants, servicePrincipals } = data;
  const now = Date.now();
  const warnCut = now + EXPIRY_WARN_DAYS * 86400000;
  const results: ControlResult[] = [];

  const expiring: AffectedObject[] = [];
  const expired: AffectedObject[] = [];
  for (const app of apps) {
    const creds = [
      ...(app.passwordCredentials ?? []).map((c) => ({ c, type: 'secret' })),
      ...(app.keyCredentials ?? []).map((c) => ({ c, type: 'certificate' })),
    ];
    for (const { c, type } of creds) {
      if (!c.endDateTime) continue;
      const end = new Date(c.endDateTime).getTime();
      const days = Math.floor((end - now) / 86400000);
      const obj: AffectedObject = {
        type: 'application',
        id: `${app.appId ?? app.id}:${c.keyId ?? c.displayName ?? type}`,
        label: app.displayName ?? app.appId ?? app.id,
        detail: `${type} ${end < now ? `expired ${-days}d ago` : `expires in ${days}d`}`,
      };
      if (end < now) expired.push(obj);
      else if (end <= warnCut) expiring.push(obj);
    }
  }
  results.push({
    controlId: 'app_credential_expiring',
    status: expiring.length > 0 ? 'fail' : 'pass',
    affected: expiring,
    endpoints: [APP_ENDPOINTS.applications],
  });
  results.push({
    controlId: 'app_credential_expired',
    status: expired.length > 0 ? 'fail' : 'pass',
    affected: expired,
    endpoints: [APP_ENDPOINTS.applications],
  });

  // Risky tenant-wide consent.
  const spNames = new Map(servicePrincipals.map((sp) => [sp.id, sp.displayName]));
  const risky: AffectedObject[] = [];
  for (const g of grants) {
    if (g.consentType !== 'AllPrincipals') continue;
    const scopes = String(g.scope ?? '').trim().split(/\s+/).filter(Boolean);
    const high = scopes.filter((s) => HIGH_RISK_SCOPES.has(s));
    if (high.length === 0) continue;
    risky.push({
      type: 'application',
      id: g.clientId ?? 'grant',
      label: (g.clientId && spNames.get(g.clientId)) || g.clientId || 'consented app',
      detail: `tenant-wide consent: ${high.join(', ')}`,
    });
  }
  results.push({
    controlId: 'app_risky_consent',
    status: risky.length > 0 ? 'fail' : 'pass',
    affected: risky,
    endpoints: [APP_ENDPOINTS.grants, APP_ENDPOINTS.servicePrincipals],
  });

  // Multi-tenant / personal-account audiences.
  const multi: AffectedObject[] = apps
    .filter((a) => a.signInAudience && a.signInAudience !== 'AzureADMyOrg')
    .map((a) => ({
      type: 'application',
      id: a.appId ?? a.id,
      label: a.displayName ?? a.appId ?? a.id,
      detail: `audience: ${a.signInAudience}`,
    }));
  results.push({
    controlId: 'app_multitenant',
    status: multi.length > 0 ? 'fail' : 'pass',
    affected: multi,
    endpoints: [APP_ENDPOINTS.applications],
  });

  return results;
}

export async function auditApplications(): Promise<ControlResult[]> {
  return evaluateApplications(await collectApplicationsData());
}
